'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { ButtonLink, Icon, type IconName, MaskType, MaskVariant } from '~/components/primitives';
import { useMemo } from 'react';
import { I18N_CONF } from '@funkey/locales/config';
import type { ISbStoryData } from '@storyblok/react';
import Title from '~/components/utils/Title';
import { useI18n } from '~/features/i18n';
import type { action_getFeaturedActivityCategories } from '~/app/[locale]/(web)/_data/actions';
import type { SB_AssetProps, SB_HeaderMenuItemProps, SB_HeaderProductMenuItemProps, SB_SubNavBlockProps, SB_SubNavItemBlockProps, SB_SubNavSpotlightBlockProps } from '~/storyblok/components/types.generated';
import { transformStoryblokLink } from '~/storyblok/components/utils';
import { StoryblokLink } from '~/storyblok/utils/StoryblokLink';
import { NavigationMenuItem, NavigationMenuLink, navigationMenuTriggerPillStyle } from '~/components/primitives/NavigationMenu';
import { NavigationMenuContent, NavigationMenuTrigger } from '~/components/primitives/NavigationMenu';
import { StoryblokUtils } from '~/storyblok/utils';
import { cn } from '~/utils';
type WebHeaderMenuItemProps = {
  children: React.ReactNode;
  menuItem: SB_HeaderMenuItemProps | SB_HeaderProductMenuItemProps;
  mobileIcon?: IconName;
  categories: Awaited<ReturnType<typeof action_getFeaturedActivityCategories>>;
  locale: I18N_CONF.Locale;
};
export default function WebHeaderMenuItem(props: WebHeaderMenuItemProps) {
  const label = <span className="text-sm font-bold text-white">{props.children}</span>;
  const content = useMemo<React.ReactNode>(() => {
    if (props.menuItem.component !== 'HeaderMenuItem' || !props.menuItem.subNavItems?.length) {
      return null;
    }
    switch (props.menuItem.layout) {
      case 'SUBNAV_SPOTLIGHT':
        return <SubMenuActivities categories={props.categories} items={props.menuItem.subNavItems} bgAsset={props.menuItem.subNavBgAsset} />;
      case 'SUBNAV_COLS':
        return <SubMenuProducts locale={props.locale} items={props.menuItem.subNavItems} />;
      case 'SUBNAV_ITEMS':
        return <SubMenuPartners items={props.menuItem.subNavItems} />;
      case '':
      case undefined:
        return null;
      default:
        props.menuItem.layout satisfies never;
        return null;
    }
  }, [props.menuItem, props.categories]);
  const navClass =
  // The nav row is 6rem; the `header` token also counts the USP bar above it.
  'relative flex h-24 items-center justify-between [data-highlighted]:bg-gray-900';
  return <NavigationMenuItem className="group" value={props.menuItem._uid}>
            {'subNavItems' in props.menuItem && !!props.menuItem.subNavItems?.length ? <>
                    <span className={navClass}>
                        <NavigationMenuTrigger className="relative px-0 xl:px-2 2xl:px-4">
                            {label}
                        </NavigationMenuTrigger>
                    </span>

                    <div className="sr-only">{content}</div>

                    <NavigationMenuContent className="relative left-1/2 container flex -translate-x-1/2 overflow-hidden rounded-4xl bg-white px-0 shadow-2xl">
                        {content}
                    </NavigationMenuContent>
                </> : <NavigationMenuLink asChild>
                    <StoryblokLink type="link" link={props.menuItem.link} className={cn(navClass, 'pointer-events-none')}>
                        <div className={navigationMenuTriggerPillStyle({
          variant: 'ghost'
        })}>
                            {label}
                        </div>
                    </StoryblokLink>
                </NavigationMenuLink>}
        </NavigationMenuItem>;
}
const SubMenuActivities: React.FC<{
  items: Array<SB_SubNavItemBlockProps | SB_SubNavBlockProps | SB_SubNavSpotlightBlockProps>;
  categories: Awaited<ReturnType<typeof action_getFeaturedActivityCategories>>;
  bgAsset?: SB_AssetProps;
}> = t0 => {
  const $ = _c(46);
  const {
    categories,
    items,
    bgAsset
  } = t0;
  const i18n = useI18n();
  let t1;
  if ($[0] !== items) {
    t1 = items?.find(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const spotlightItem = t1 as SB_SubNavSpotlightBlockProps | undefined;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== categories || $[3] !== i18n || $[4] !== items) {
    const subNavItems = items?.filter(_temp2) ?? [];
    t4 = "z-1 flex gap-9 pt-16 pb-16 pl-16";
    let t6;
    if ($[9] !== i18n) {
      t6 = i18n._("Categories");
      $[9] = i18n;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== t6) {
      t7 = <p className="font-bold text-blue">{t6}</p>;
      $[11] = t6;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== categories || $[14] !== i18n.locale) {
      t8 = categories?.map(category => <NavigationMenuLink asChild={true} key={category?.id}><Link href={I18N_CONF.getTranslatedUrl("categories", {
          slug: category.slug,
          locale: i18n.locale
        })} className="group flex cursor-pointer flex-row gap-2 text-primary transition-all hover:text-turqoise">{category?.name}<Icon name="arrow-right" aria-hidden="true" className="-translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" /></Link></NavigationMenuLink>);
      $[13] = categories;
      $[14] = i18n.locale;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t8) {
      t9 = <div className="z-1 mt-8 flex flex-col gap-4">{t8}</div>;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== i18n.locale) {
      t10 = I18N_CONF.getTranslatedUrl("teambuildings", {
        locale: i18n.locale
      });
      $[18] = i18n.locale;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== i18n) {
      t11 = i18n._("All activities");
      $[20] = i18n;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Icon name="arrow-right" aria-hidden="true" className="-translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    let t13;
    if ($[23] !== t10 || $[24] !== t11) {
      t13 = <NavigationMenuLink asChild={true}><Link href={t10} className="group mt-4 flex cursor-pointer flex-row gap-2 text-orange transition-all hover:text-turqoise">{t11}{t12}</Link></NavigationMenuLink>;
      $[23] = t10;
      $[24] = t11;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    if ($[26] !== t13 || $[27] !== t7 || $[28] !== t9) {
      t5 = <div className="w-52">{t7}{t9}{t13}</div>;
      $[26] = t13;
      $[27] = t7;
      $[28] = t9;
      $[29] = t5;
    } else {
      t5 = $[29];
    }
    t2 = "w-52";
    let t14;
    if ($[30] !== i18n.locale) {
      t14 = item_1 => <div className="flex flex-col gap-4" key={item_1._uid}><p className="font-bold text-blue">{item_1.title}</p>{item_1.subNav?.map(item_2 => <NavigationMenuLink key={item_2.name} asChild={true}><StoryblokLink className="group flex cursor-pointer flex-row items-center gap-2 text-primary transition-all hover:text-turqoise" type="story" link={item_2}>{StoryblokUtils.Story.translatedName(item_2, i18n.locale)}<Icon name="arrow-right" aria-hidden="true" className="-translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" /></StoryblokLink></NavigationMenuLink>)}</div>;
      $[30] = i18n.locale;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    t3 = subNavItems.map(t14);
    $[2] = categories;
    $[3] = i18n;
    $[4] = items;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[32] !== t2 || $[33] !== t3) {
    t6 = <div className={t2}>{t3}</div>;
    $[32] = t2;
    $[33] = t3;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== spotlightItem) {
    t7 = spotlightItem ? <div className="z-1 ml-10 flex max-w-xs min-w-[300px] flex-col gap-6 rounded-2xl bg-brown-100 p-10 xl:ml-25">{spotlightItem.asset?.filename ? <div className="relative h-12 w-25"><Image src={spotlightItem.asset.filename} alt={spotlightItem.asset.alt || spotlightItem.asset.filename} fill={true} /></div> : null}<p className="text-blue">{spotlightItem.text}</p>{spotlightItem.link ? <NavigationMenuLink asChild={true}><ButtonLink href={`/${spotlightItem.link.cached_url}`} iconAfter="arrow-right" size="lg" variant="tertiary">{spotlightItem?.linkText}</ButtonLink></NavigationMenuLink> : null}</div> : null;
    $[35] = spotlightItem;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== bgAsset || $[38] !== i18n) {
    t8 = bgAsset?.filename ? <Image src={bgAsset.filename} alt={i18n._("Navigationbar image")} width={900} height={800} className={cx("absolute top-0 -right-96 z-0 xl:-right-64", MaskVariant({
      mask: MaskType.Mask2
    }))} /> : null;
    $[37] = bgAsset;
    $[38] = i18n;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] !== t4 || $[41] !== t5 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8) {
    t9 = <ul className={t4}>{t5}{t6}{t7}{t8}</ul>;
    $[40] = t4;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  return t9;
};
const SubMenuPartners: React.FC<{
  items: Array<SB_SubNavItemBlockProps | SB_SubNavBlockProps | SB_SubNavSpotlightBlockProps>;
}> = props => {
  const $ = _c(4);
  let t0;
  if ($[0] !== props.items) {
    t0 = props.items.map(_temp3);
    $[0] = props.items;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <div className="grid grid-cols-2 p-10">{t0}</div>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const SubMenuProducts: React.FC<{
  locale: I18N_CONF.Locale;
  items: Array<SB_SubNavItemBlockProps | SB_SubNavBlockProps | SB_SubNavSpotlightBlockProps>;
}> = props => {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.items || $[1] !== props.locale) {
    t0 = props.items?.map(item => <div key={item._uid} className={cx("relative flex w-1/3 flex-col gap-3 overflow-hidden pt-16 pb-12 pl-16 text-blue", BackgroundColorVariant[item?.bgColor || "WHITE"])}><Title size="lg" weight="bold" className="group flex items-center transition-all hover:text-secondary"><span>{item.title}</span><Icon name="arrow-right" aria-hidden="true" className="ml-1 opacity-0 transition-all group-hover:ml-2 group-hover:text-secondary group-hover:opacity-100" /></Title><p className="w-48 text-sm">{item.text}</p>{"link" in item && !!item.link && <NavigationMenuLink asChild={true}><ButtonLink {...transformStoryblokLink(item.link)} variant="secondary" className="mr-auto mb-3">{item.linkText}</ButtonLink></NavigationMenuLink>}{"subNav" in item && (item.subNav as undefined | Array<string | ISbStoryData<{}>>)?.map(subNavItem => {
        if (typeof subNavItem === "string") {
          return null;
        }
        return <NavigationMenuLink asChild={true} key={subNavItem.uuid}><Link className="text-sm transition-all hover:text-secondary" href={`/${subNavItem.full_slug}`}>{StoryblokUtils.Story.translatedName(subNavItem, props.locale)}</Link></NavigationMenuLink>;
      })}{"asset" in item && !!item.asset?.filename && <div className="absolute top-1/2 right-0 z-0 h-52 w-32 -translate-y-1/2 xl:h-72 xl:w-52"><Image src={item.asset.filename} alt={item.asset.alt || item.asset.filename} fill={true} className="object-contain object-right" /></div>}</div>);
    $[0] = props.items;
    $[1] = props.locale;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <div className="flex w-full flex-row">{t0}</div>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const IconColorVariant = {
  GREEN: 'text-secondary',
  YELLOW: 'text-yellow-600',
  SALMON: 'text-salmon-500',
  BLUE: 'text-blue-800'
} as const;
const BackgroundColorVariant = {
  WHITE: 'bg-white',
  BROWN: 'bg-brown-100',
  GRAY: 'bg-blue-100'
} as const;
function _temp(item) {
  return item.component === "SubNavSpotlightBlock";
}
function _temp2(item_0) {
  return item_0.component === "SubNavBlock";
}
function _temp3(item) {
  return "subNavItem" in item ? <NavigationMenuLink asChild={true} key={item._uid}><StoryblokLink type="story" link={item.subNavItem as ISbStoryData<unknown>} className="group flex flex-row items-center gap-8 rounded-4xl px-8 py-6 text-primary transition-colors hover:bg-blue-100">{item.icon ? <Icon name={item.icon as Icon.Name} viewBox="0 0 48 48" size="3xl" className={IconColorVariant[item?.iconColor as keyof typeof IconColorVariant || "GREEN"]} /> : null}<div className="flex flex-col gap-1"><Title weight="bold" size="lg">{item.title}</Title><p className="text-blue-500 group-hover:text-blue">{item.text}</p></div></StoryblokLink></NavigationMenuLink> : null;
}